import { EventEmitter } from 'events';
import camelCase from 'lodash/camelCase';

export enum SlideshowMediaEvents {
  Ready = 'loadedmetadata',
  Error = 'error',
  Playing = 'playing',
  Pause = 'pause',
  Ended = 'ended',
  Timeupdate = 'timeupdate',
  Progress = 'progress',
}

class SlideshowMedia extends EventEmitter {
  private _autoplay!: boolean;
  private _controls!: boolean;
  private _crossOrigin!: string | null;
  private _currentTime!: number;
  private _defaultMuted!: boolean;
  private _defaultPlaybackRate!: number;
  private _loop!: boolean;
  private _muted!: boolean;
  private _playbackRate!: number;
  private _preload!: string;
  private _src!: string;
  private _srcObject!: MediaProvider | null;
  private _volume!: number;
  private _paused!: boolean;
  private _currentSrc!: string;
  private _duration!: number;

  private timer: any;
  private tickDelta: number;

  /**
   * Gets a collection of buffered time ranges.
   */
  readonly buffered!: TimeRanges;

  /**
   * Gets information about whether the playback has ended or not.
   */
  private _ended!: boolean;
  /**
   * Returns an object representing the current error state of the audio or video element.
   */

  readonly error!: MediaError | null;
  readonly mediaKeys!: MediaKeys | null;
  /**
   * Gets the current network activity for the element.
   */

  readonly networkState!: number;

  /**
   * Gets TimeRanges for the current media resource that has been played.
   */

  readonly played!: TimeRanges;
  readonly readyState!: number;
  /**
   * Returns a TimeRanges object that represents the ranges of the current media resource that can be seeked.
   */

  readonly seekable!: TimeRanges;
  /**
   * Gets a flag that indicates whether the client is currently moving to a new playback position in the media resource.
   */

  readonly seeking!: boolean;
  readonly textTracks!: TextTrackList;
  readonly HAVE_CURRENT_DATA!: number;
  readonly HAVE_ENOUGH_DATA!: number;
  readonly HAVE_FUTURE_DATA!: number;
  readonly HAVE_METADATA!: number;
  readonly HAVE_NOTHING!: number;
  readonly NETWORK_EMPTY!: number;
  readonly NETWORK_IDLE!: number;
  readonly NETWORK_LOADING!: number;
  readonly NETWORK_NO_SOURCE!: number;

  constructor() {
    super();
    this._autoplay = false;
    this._controls = false;
    this._crossOrigin = null;
    this._currentTime = 0;
    this._duration = 0;
    this._ended = false;
    this._defaultMuted = false;
    this._defaultPlaybackRate = 1;
    this._loop = false;
    this._muted = false;
    this._playbackRate = 1;
    this._preload = '';
    this._src = '';
    this._srcObject = null;
    this._volume = 1;
    this._paused = true;

    this.readyState = 1;

    this.tickDelta = 5000;
  }

  private cancelTick() {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  private planNextTick() {
    this.cancelTick();

    if (this._currentTime >= this._duration) {
      this.tick(0);
    }

    this.timer = setTimeout(this.tick, this.tickDelta);
  }

  private tick = (time?: number) => {
    time = Number(time);
    const isDefinedTime = !isNaN(time);
    const hasScheduledTick = isDefinedTime && !!this.timer;

    if (isDefinedTime) {
      this.cancelTick();

      this._currentTime = time;
    } else {
      this._currentTime += 1;
    }

    if (this._currentTime >= this._duration) {
      if (this.loop) {
        this._currentTime = 0;
      } else {
        this.pause();
      }
    }

    this.emit(SlideshowMediaEvents.Timeupdate, {
      currentTime: this._currentTime,
      duration: this._duration,
    });

    if (!this.paused || hasScheduledTick) {
      this.planNextTick();
    }
  };

  addEventListener(eventName: string, listener: (...args: any[]) => void, options?: { once: boolean }) {
    if (options?.once) {
      return this.once(eventName, listener);
    }

    return this.on(eventName, listener);
  }

  removeEventListener(eventName: string, listener: (...args: any[]) => void) {
    return this.off(eventName, listener);
  }

  dispatchEvent(eventName: string, data: any) {
    return this.emit(eventName, data);
  }

  setAttribute(key: keyof SlideshowMedia, value: any) {
    // @ts-ignore
    this[camelCase(key)] = value;
  }

  remove() {}

  // onencrypted: ((this: HTMLMediaElement, ev: MediaEncryptedEvent) => any) | null;
  // onwaitingforkey: ((this: HTMLMediaElement, ev: Event) => any) | null;
  addTextTrack() {}
  /**
   * Returns a string that specifies whether the client can play a given media resource type.
   */
  canPlayType() {}
  fastSeek(): void {}
  /**
   * Resets the audio or video object and loads a new media resource.
   */
  load(): void {}
  /**
   * Pauses the current playback and sets paused to TRUE. This can be used to test whether the media is playing or paused. You can also use the pause or play events to tell whether the media is playing or not.
   */
  pause(): void {
    this.cancelTick();
    this._paused = true;
    this.emit(SlideshowMediaEvents.Pause);
  }
  /**
   * Loads and starts playback of a media resource.
   */
  async play(): Promise<void> {
    this._paused = false;
    this.planNextTick();
    this.emit(SlideshowMediaEvents.Playing);
    return;
  }

  async setMediaKeys(mediaKeys: MediaKeys | null): Promise<void> {
    return;
  }

  /**
   * Gets or sets a value that indicates whether to start playing the media automatically.
   */

  get autoplay() {
    return this._autoplay;
  }
  set autoplay(value) {
    this._autoplay = value;
  }

  /**
   * Gets or sets a flag that indicates whether the client provides a set of controls for the media (in case the developer does not include controls for the player).
   */

  get controls() {
    return this._controls;
  }
  set controls(value) {
    this._controls = value;
  }

  get crossOrigin() {
    return this._crossOrigin;
  }
  set crossOrigin(value) {
    this._crossOrigin = value;
  }

  /**
   * Gets or sets the current playback position, in seconds.
   */

  get currentTime() {
    return this._currentTime;
  }
  set currentTime(value) {
    this.tick(Math.ceil(value));
  }

  get defaultMuted() {
    return this._defaultMuted;
  }
  set defaultMuted(value) {
    this._defaultMuted = value;
  }

  /**
   * Gets or sets the default playback rate when the user is not using fast forward or reverse for a video or audio resource.
   */
  get defaultPlaybackRate() {
    return this._defaultPlaybackRate;
  }
  set defaultPlaybackRate(value) {
    this._defaultPlaybackRate = value;
  }

  /**
   * Gets or sets a flag to specify whether playback should restart after it completes.
   */
  get loop() {
    return this._loop;
  }
  set loop(value) {
    this._loop = value;
  }

  /**
   * Gets or sets a flag that indicates whether the audio (either audio or the audio track on video media) is muted.
   */
  get muted() {
    return this._muted;
  }
  set muted(value) {
    this._muted = value;
  }
  /**
   * Gets a flag that specifies whether playback is paused.
   */
  get paused() {
    return this._paused;
  }
  /**
   * Gets or sets the current rate of speed for the media resource to play. This speed is expressed as a multiple of the normal speed of the media resource.
   */
  get playbackRate() {
    return this._playbackRate;
  }
  set playbackRate(value) {
    this._playbackRate = value;
  }

  /**
   * Gets or sets the current playback position, in seconds.
   */
  get preload() {
    return this._preload;
  }
  set preload(value) {
    this._preload = value;
  }

  /**
   * The address or URL of the a media resource that is to be considered.
   */
  get src() {
    return this._src;
  }
  set src(value: any) {
    this._duration = value;
    this.emit(SlideshowMediaEvents.Ready);
    this.tick(0);
  }

  get srcObject() {
    return this._srcObject;
  }
  set srcObject(value) {
    this._srcObject = value;
  }

  /**
   * Gets or sets the volume level for audio portions of the media element.
   */
  get volume() {
    return this._volume;
  }
  set volume(value) {
    this._volume = value;
  }

  /**
   * Gets the address or URL of the current media resource that is selected by IHTMLMediaElement.
   */
  get currentSrc() {
    return this._currentSrc;
  }

  /**
   * Returns the duration in seconds of the current media resource. A NaN value is returned if duration is not available, or Infinity if the media resource is streaming.
   */

  get duration() {
    return this._duration;
  }
}

export default SlideshowMedia;
